import { Field, Image, RichText, Text, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';

import { ComponentProps, SitecoreContextValue } from 'src/lib/ComponentProps';

import { mapLMSCourseToContentDetails } from 'src/mappers/models/lmsCourseMapper';

import { ContentDetailItem } from 'src/types/ContentDetailsItem';

import { FieldEditFrame } from 'src/utils/customFieldEditFrame';

import Accordion from 'src/components/basicContent/_Accordion/Accordion';
import ContentOverviewHeaderContainer from 'src/components/contentOverview/_contentOverviewHeaderContainer/ContentOverviewHeaderContainer';
import ContentOverviewActionButton from 'src/components/contentOverview/contentOverviewActionButton/ContentOverviewActionButton';
import { EditorHelp } from 'src/components/layout/_EditorHelp/_EditorHelp';

import ChevronDownIconSvg from 'public/icons/ChevronDown.svg';

import { CourseFields } from '../CourseFields';

import { CourseContentItem } from './CourseOverviewContentTypes';

import styles from './CourseOverviewContents.module.css';

type CourseOverviewContentsProps = ComponentProps & {
	fields: CourseFields & {
		CourseContentsTitle: Field<string>;
		Description: Field<string>;
		CourseContentsItems: CourseContentItem[];
	};
};

export default function CourseOverviewContents(props: CourseOverviewContentsProps): JSX.Element {
	const { sitecoreContext } = useSitecoreContext<SitecoreContextValue>();

	const contentDetailsItem: ContentDetailItem | null = mapLMSCourseToContentDetails(sitecoreContext.itemId ?? '', props.rendering.fields);

	if (!contentDetailsItem) {
		return <></>;
	}

	const isTwoColumnDetailList = (courseContentsItem: CourseContentItem) => {
		return courseContentsItem?.DisplayDetailsInTwoColumns?.value;
	};

	return (
		<section className="maxWidth pad" data-display-pdf-download>
			<ContentOverviewHeaderContainer header={props.fields?.CourseContentsTitle?.value} />

			{props.fields?.CourseContentsItems?.filter((courseContentsItem) => courseContentsItem).map((courseContentsItem, index) => (
				<Accordion
					key={index}
					sectionClass={styles.accordion}
					headerClass={styles.accordionHeader}
					bodyClass={`${styles.accordionBody} ${
						courseContentsItem.Title || courseContentsItem.Description || courseContentsItem.Image ? styles.accordionBodyTwoColumns : ''
					}`}
				>
					<>
						<Text field={courseContentsItem.Title} />

						<span>
							<ChevronDownIconSvg className={styles.chevron} />
						</span>
					</>

					<div>
						<Image media={courseContentsItem.Image} className={styles.image} />

						<Text field={courseContentsItem.Subtitle} className={styles.subtitle} tag="h3" />

						<RichText field={courseContentsItem.Description} className={styles.richText} />

						{!courseContentsItem.HideButton?.value && (
							<div className={styles.actionButton}>
								<ContentOverviewActionButton removePadding {...props} />
							</div>
						)}
					</div>

					<div>
						<RichText field={courseContentsItem.DetailsDescription} className={styles.richText} />

						<FieldEditFrame itemId={sitecoreContext?.itemId ?? undefined} fields={['DisplayDetailsInTwoColumns']} header="Edit Column">
							<EditorHelp text={'Click here to change between one or two column layout'} indent />
							<ul className={`${styles.list} ${isTwoColumnDetailList(courseContentsItem) ? styles.listTwoColumns : ''}`}>
								{courseContentsItem.CourseContentsDetailItems?.map((detail, index) => (
									<li key={index} className={styles.item}>
										{detail.Icon?.fields?.Svg && (
											<span className={styles.icon}>
												<Image media={detail.Icon?.fields?.Svg} className={styles.icon} />
											</span>
										)}

										<div className={styles.detailText}>
											<Text tag="small" field={detail.Label} />
											<Text tag="p" field={detail.Text} />
										</div>
									</li>
								))}
							</ul>
						</FieldEditFrame>
					</div>
				</Accordion>
			))}
		</section>
	);
}
